import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { ro } from "date-fns/locale";
import { cn } from "@/lib/utils";

/**
 * DateInput — Romanian zz/ll/an picker.
 * value: string "YYYY-MM-DD" (or full ISO) | "" | null
 * onChange(v): emits "YYYY-MM-DD" or "" when cleared
 *
 * Renders a button trigger styled like a text input + Shadcn calendar popover.
 * Always displays dd/MM/yyyy regardless of browser locale.
 */
export default function DateInput({
  value,
  onChange,
  placeholder = "zz/ll/an",
  className = "",
  testid,
  disabled,
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);

  const parsed = parseValue(value);
  const display = parsed ? formatDDMMYYYY(parsed) : "";

  function handleSelect(d) {
    if (!d) return;
    onChange?.(toYMD(d));
    setOpen(false);
  }

  function clear(e) {
    e.stopPropagation();
    onChange?.("");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full inline-flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-[#0047FF] disabled:opacity-50",
            !display && "text-slate-400",
            className,
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarIcon className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="truncate tabular">{display || placeholder}</span>
          </span>
          {allowClear && display && !disabled && (
            <span
              role="button"
              aria-label="Șterge data"
              onClick={clear}
              className="text-slate-400 hover:text-slate-700 p-0.5 -mr-1"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed || undefined}
          onSelect={handleSelect}
          locale={ro}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function parseValue(v) {
  if (!v) return null;
  // accept "YYYY-MM-DD" or full ISO
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function pad(n) { return String(n).padStart(2, "0"); }

function toYMD(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDDMMYYYY(d) {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}
